/**
 * Single source of truth for all investment opportunities page analytics event
 * names.
 *
 * Names follow the object-action framework: `<l1_product>_<l2_component>_<action>`,
 * snake case, lowercase. L1 is the page (`investment_opportunities`), L2 the
 * component on it (`asset_card`, `see_all`).
 *
 * The page renders four heterogeneous rails from three different data sources,
 * so the rail is a property (`asset_type`) rather than four separate card-click
 * events — one event, many properties.
 */

export const INVESTMENT_OPPORTUNITIES_EVENTS = {
  /** Any asset card in any rail. Carries `asset_type` + `slug`. */
  ASSET_CARD_CLICKED: 'investment_opportunities_asset_card_clicked',
  /**
   * The "See all" link — both the section header link and the terminal rail
   * tile. Carries `asset_type` for which rail the user wants to see all of.
   */
  SEE_ALL_CLICKED: 'investment_opportunities_see_all_clicked',
  /** Bookmark toggle on an offering card. Carry `asset_type` + `slug`. */
  ASSET_FOLLOWED: 'investment_opportunities_asset_followed',
  ASSET_UNFOLLOWED: 'investment_opportunities_asset_unfollowed',
} as const;

export type TInvestmentOpportunitiesEventName =
  (typeof INVESTMENT_OPPORTUNITIES_EVENTS)[keyof typeof INVESTMENT_OPPORTUNITIES_EVENTS];

/**
 * The page view, reported through Segment `page()` rather than as a `track`
 * event — every other page in the app does the same, so
 * /investment-opportunities shows up in the existing pageview reporting instead
 * of needing its own query.
 */
export const INVESTMENT_OPPORTUNITIES_PAGE = {
  category: 'investment-opportunities',
  name: 'index',
} as const;

/**
 * The surfaces these events can be fired from, per the event metadata rules.
 *
 * The rails are not exclusive to /investment-opportunities — the homepage
 * renders the real-world assets and growth-stage ones too — so `source` names
 * which surface the click happened on rather than being a page-wide constant.
 * The event names stay shared: it is the same rail, answering the same question.
 *
 * `investor_id`, the other required field, is not passed per event — Segment
 * `identify()` attaches it at login, so every event inherits it.
 */
export const INVESTMENT_OPPORTUNITIES_SOURCES = {
  PAGE: 'investment_opportunities',
  HOMEPAGE: 'homepage',
} as const;

export type TInvestmentOpportunitiesSource =
  (typeof INVESTMENT_OPPORTUNITIES_SOURCES)[keyof typeof INVESTMENT_OPPORTUNITIES_SOURCES];

/** `platform` is web for every event fired from r-client. */
export const INVESTMENT_OPPORTUNITIES_PLATFORM = 'web';

/**
 * Flat array of every investment opportunities event name. The hook spec
 * (`use-investment-opportunities-analytics.spec.ts`) iterates this to assert
 * each name is dispatched by at least one
 * `useInvestmentOpportunitiesAnalytics` method.
 */
export const ALL_INVESTMENT_OPPORTUNITIES_EVENT_NAMES = Object.values(
  INVESTMENT_OPPORTUNITIES_EVENTS
);
